import {CSSProperties, memo, useMemo, ReactNode} from "react";
import {CELL_SIZE} from "../../utility/types";
import {Vec2} from "../../utility/math/vec2";

export type LabelVariant = "solid" | "subtle";

/** A general label + description used for both nodes and connections. */
export const Label = memo(
  ({
    coords,
    label,
    description,
    height = 64,
    variant = "solid",
    footer,
  }: {
    coords: Vec2;
    label: string;
    description?: string;
    height?: number;
    variant?: LabelVariant;
    footer?: ReactNode;
  }) => {
    const worldPos = useMemo(() => coords.mulScalar(CELL_SIZE).addScalar(0.5 * CELL_SIZE), [coords]);
    const hasConnectingLine = height !== 0;

    const outerContainerStyle = useMemo<CSSProperties>(
      () => ({
        position: "absolute",
        top: 0,
        left: 0,
        transform: `translate(${worldPos.x}px, ${worldPos.y - height}px) translate(-50%, ${
          hasConnectingLine ? "-100%" : "-50%"
        })`,
      }),
      [hasConnectingLine, height, worldPos.x, worldPos.y],
    );

    const containerStyle = useMemo<CSSProperties>(
      () => ({
        pointerEvents: "auto", // Allow clicks on label content (including citation badges)
        cursor: "default", // Show default cursor instead of pan/grab cursor
        backgroundColor: "white",
        borderWidth: "1px",
        borderColor: "#e2e8f0",
        borderRadius: "4px",
        padding: "4px 8px",
        boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
        margin: 0,
        display: "flex",
        flexFlow: "column nowrap",
        gap: "4px",
        maxWidth: "240px",
        overflow: "hidden",
      }),
      [],
    );

    const labelStyle = useMemo<CSSProperties>(
      () => ({
        fontSize: "12px",
        fontWeight: variant === "solid" ? "500" : "400",
        color: variant === "solid" ? "var(--chakra3-colors-fg)" : "var(--chakra3-colors-fg-muted)",
        fontStyle: variant === "solid" ? "" : "italic",
      }),
      [variant],
    );

    const descriptionStyle = useMemo<CSSProperties>(
      () => ({fontSize: "10px", color: "#6b7280", display: "-webkit-box", overflow: "hidden", whiteSpace: "pre-wrap"}),
      [],
    );

    const lineStyle = useMemo<CSSProperties>(
      () => ({
        position: "absolute",
        top: "100%",
        left: "50%",
        width: "1px",
        height: `${height}px`,
        backgroundColor: "#e2e8f0",
        borderLeft: "1px dashed #9ca3af",
        transform: "translateX(-50%)",
      }),
      [height],
    );

    const hasLabel = useMemo(() => label.trim().length > 0, [label]);
    const hasDescription = useMemo(() => description != null && description.trim().length > 0, [description]);

    if (!hasLabel && !hasDescription) {
      return null;
    }

    return (
      <div style={outerContainerStyle}>
        <div style={containerStyle}>
          <span style={labelStyle}>{label}</span>
          {hasDescription && <span style={descriptionStyle}>{description}</span>}
          {footer}
        </div>
        {hasConnectingLine && <div style={lineStyle} />}
      </div>
    );
  },
);
